import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import {
  Send as SendIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

const LoadingDots = () => (
  <Box 
    sx={{ 
      display: 'flex',
      gap: 1,
      p: 1,
      '& .dot': {
        width: 8,
        height: 8,
        backgroundColor: 'grey.500',
        borderRadius: '50%',
        animation: 'dotPulse 1.5s infinite',
      },
      '& .dot:nth-of-type(2)': {
        animationDelay: '0.2s',
      },
      '& .dot:nth-of-type(3)': {
        animationDelay: '0.4s',
      },
      '@keyframes dotPulse': {
        '0%, 100%': {
          transform: 'scale(0.7)',
          opacity: 0.5,
        },
        '50%': {
          transform: 'scale(1)',
          opacity: 1,
        },
      },
    }}
  >
    <Box className="dot" />
    <Box className="dot" />
    <Box className="dot" />
  </Box>
);

const TestChat = () => {
  const { currentUser } = useAuth();
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);

  const handleSend = async () => {
    const messageText = message.trim();
    if (messageText && currentUser) {
      const newMessage = {
        text: messageText,
        sender: 'user',
        timestamp: new Date().toISOString(),
      };
      setMessages([...messages, newMessage]);
      setMessage(''); // Clear input field immediately after adding message

      try {
        setIsLoading(true);
        const response = await fetch('https://pychat-agent-rsjpn4y3ua-uc.a.run.app', {
//        const response = await fetch('http://127.0.0.1:5001/bsi-bass/us-central1/pychat_agent', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            message: messageText,
            sessionId: sessionId,
            metadata: { 
              uid: currentUser.uid,
            }
          })
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const data = await response.json();
        
        // Add bot response to messages
        const botMessage = {
          text: data.response,
          sender: 'bot',
          timestamp: new Date().toISOString(),
        };
        setMessages(msgs => [...msgs, botMessage]);
        
        // Update history with the response from API
        setSessionId(data.sessionId || null);
      } catch (error) {
        console.error('Error sending message:', error);
        setMessages(msgs => [...msgs, {
          text: 'Error: Failed to send message',
          sender: 'bot',
          timestamp: new Date().toISOString(),
        }]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleClearChat = () => {
    setMessages([]);
    setSessionId(null);
  };

  return (
    <Box sx={{ height: 'calc(100vh - 140px)', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Chat messages */}
      <Paper
        sx={{
          flex: 1,
          mb: 2,
          p: 2,
          overflow: 'auto',
          bgcolor: 'grey.50',
        }}
      >
        {messages.map((msg, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
              mb: 1,
            }}
          >
            <Paper
              sx={{
                p: 1,
                maxWidth: '70%',
                bgcolor: msg.sender === 'user' ? 'primary.main' : 'white',
                color: msg.sender === 'user' ? 'white' : 'text.primary',
              }}
            >
              {msg.sender === 'user' ? (
                <Typography variant="body1">{msg.text}</Typography>
              ) : (
                <ReactMarkdown
                  components={{
                    // Style markdown elements
                    p: ({ node, ...props }) => <Typography variant="body1" sx={{ mb: 1, '&:last-child': { mb: 0 } }} {...props} />,
                    h1: ({ node, ...props }) => <Typography variant="h5" sx={{ mb: 1, fontWeight: 'bold' }} {...props} />,
                    h2: ({ node, ...props }) => <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }} {...props} />,
                    h3: ({ node, ...props }) => <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }} {...props} />,
                    code: ({ node, className, children, ...props }) => (
                      !className ? (
                        <code style={{ 
                          backgroundColor: 'rgba(0, 0, 0, 0.05)', 
                          padding: '2px 6px', 
                          borderRadius: '3px',
                          fontFamily: 'monospace',
                          fontSize: '0.9em'
                        }} {...props}>{children}</code>
                      ) : (
                        <code style={{ 
                          display: 'block',
                          backgroundColor: 'rgba(0, 0, 0, 0.05)', 
                          padding: '12px', 
                          borderRadius: '4px',
                          fontFamily: 'monospace',
                          fontSize: '0.9em',
                          overflowX: 'auto',
                          marginTop: '8px',
                          marginBottom: '8px'
                        }} className={className} {...props}>{children}</code>
                      )
                    ),
                    ul: ({ node, ...props }) => <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }} {...props} />,
                    ol: ({ node, ...props }) => <ol style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }} {...props} />,
                    li: ({ node, ...props }) => <li style={{ marginBottom: '2px' }} {...props} />,
                    a: ({ node, ...props }) => <a style={{ color: '#1976d2', textDecoration: 'underline' }} {...props} />,
                  }}
                >
                  {msg.text}
                </ReactMarkdown>
              )}
            </Paper>
          </Box>
        ))}
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              mb: 1,
            }}
          >
            <Paper
              sx={{
                bgcolor: 'white',
              }}
            >
              <LoadingDots />
            </Paper>
          </Box>
        )}
      </Paper>

      {/* Input area */}
      <Paper
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <IconButton
          color="error"
          onClick={handleClearChat}
          disabled={messages.length === 0}
          sx={{ width: 'auto' }}
          title="Clear chat"
        >
          <DeleteIcon />
        </IconButton>
        <TextField
          fullWidth
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Type your message..."
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleSend();
            }
          }}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={!message.trim()}
          sx={{ width: 'auto' }}
        >
          <SendIcon />
        </IconButton>
      </Paper>
    </Box>
  );
};

export default TestChat;
